import type { Trip, TripDestination } from '@/services/trips';
import {
  diffCalendarDaysInTimezone,
  diffTripCalendarDateKeys,
  getDateKeyForTripDay,
  getTripDayNumberForInstant,
  getTripTotalDays,
  startOfDayInTimezone,
} from '@/utils/tripCalendar';
import { resolveTripSurfaceTimezone, toDateKeyInTimezone } from '@/utils/tripItemSchedule';

export {
  diffCalendarDaysInTimezone,
  getDateKeyForTripDay,
  getItemTripCalendarDateKey,
  getItemTripDayNumber,
  getTripDayNumberForInstant,
  getTripEndDateKey,
  getTripStartDateKey,
  getTripTotalDays,
  startOfDayInTimezone,
} from '@/utils/tripCalendar';

export type TripLifecycleState = 'upcoming' | 'active' | 'completed';

export type TripDayContext = {
  state: TripLifecycleState;
  /** IANA timezone used for trip-day boundaries (destination-local). */
  timeZone: string;
  todayKey: string;
  currentDay: number | null;
  totalDays: number;
  progressPercent: number;
  daysUntilStart: number;
  daysSinceEnd: number;
  activeDestination: TripDestination | null;
  activeDestinationIndex: number | null;
};

export type TripDayContextOptions = {
  timeZone?: string;
};

const destinationForDate = (
  trip: Trip,
  date: Date,
  timeZone: string,
): { destination: TripDestination | null; index: number | null } => {
  const destinations = trip.destinations ?? [];
  if (!destinations.length) {
    return { destination: null, index: null };
  }

  const target = startOfDayInTimezone(date, timeZone).getTime();
  const index = destinations.findIndex((destination) => {
    const start = startOfDayInTimezone(destination.startDate, timeZone).getTime();
    const end = startOfDayInTimezone(destination.endDate, timeZone).getTime();
    return target >= start && target <= end;
  });

  if (index >= 0) {
    return { destination: destinations[index], index };
  }

  return { destination: destinations[0], index: 0 };
};

export const getTripDayContext = (
  trip: Trip,
  now: Date = new Date(),
  options: TripDayContextOptions = {},
): TripDayContext => {
  const timeZone = options.timeZone ?? resolveTripSurfaceTimezone(trip);
  const totalDays = getTripTotalDays(trip);
  const tripStartKey = getDateKeyForTripDay(trip, 1);
  const tripEndKey = getDateKeyForTripDay(trip, totalDays);
  const destinationTodayKey = toDateKeyInTimezone(now, timeZone);
  const today = startOfDayInTimezone(now, timeZone);

  const state: TripLifecycleState =
    destinationTodayKey < tripStartKey
      ? 'upcoming'
      : destinationTodayKey > tripEndKey
        ? 'completed'
        : 'active';

  const currentDay = state === 'active' ? getTripDayNumberForInstant(trip, now, timeZone) : null;
  const daysUntilStart =
    state === 'upcoming' ? Math.max(0, diffTripCalendarDateKeys(destinationTodayKey, tripStartKey)) : 0;
  const daysSinceEnd =
    state === 'completed' ? Math.max(0, diffTripCalendarDateKeys(tripEndKey, destinationTodayKey)) : 0;

  const progressPercent =
    state === 'upcoming'
      ? 0
      : state === 'completed'
        ? 100
        : Math.min(100, Math.max(1, Math.round(((currentDay ?? 1) / totalDays) * 100)));

  const { destination, index } = destinationForDate(trip, state === 'upcoming' ? trip.startDate : today, timeZone);

  const todayKey =
    state === 'active' && currentDay ? getDateKeyForTripDay(trip, currentDay) : destinationTodayKey;

  return {
    state,
    timeZone,
    todayKey,
    currentDay,
    totalDays,
    progressPercent,
    daysUntilStart,
    daysSinceEnd,
    activeDestination: destination,
    activeDestinationIndex: index,
  };
};

export const isDateOnTripDay = (date: Date, dayContext: TripDayContext): boolean => {
  return toDateKeyInTimezone(date, dayContext.timeZone) === dayContext.todayKey;
};
